import { useState } from 'react'

import CancelIcon from '@mui/icons-material/Cancel'
import EditIcon from '@mui/icons-material/Edit'
import InfoIcon from '@mui/icons-material/Info'
import LaunchIcon from '@mui/icons-material/Launch'
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { EntityKpi } from '@components/EntityKpi'
import { StickyDeadlineIcon } from '@components/StickyDeadlineIcon'
import { formatDateTime } from '@util/date-util'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import { Due } from '../../../theme/components/Due'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import type { Activiteit } from '../types/Activiteit'

import { ActiviteitLog } from './ActiviteitLog'
import { ActiviteitOpmerking } from './ActiviteitOpmerking'
import { WowDrawer } from './WowDrawer'

export { ActiviteitenTableRow }

const statusLabels = {
  open: 'Open',
  done: 'Afgerond',
  cancelled: 'Geannuleerd',
  retry: 'Opnieuw proberen',
  failed: 'Gefaald',
} as const

const getStatusLabel = (status) => {
  const label = statusLabels[status]

  return label || status
}

type Props = {
  activiteit: Activiteit
  user: AuthenticatedUser
  handleClickFinish: (args: {
    key: string
    type: 'resolve' | 'cancel'
    activiteit: Activiteit
  }) => void
  handleClickEdit: () => void
  handleClickCancel: (args: {
    key: string
    type: 'cancel'
    activiteit: Activiteit
  }) => void
  isVerantwoordelijke: boolean
}

function ActiviteitenTableRow({
  activiteit,
  user,
  handleClickFinish,
  handleClickEdit,
  handleClickCancel,
  isVerantwoordelijke,
}: Props) {
  const {
    _id,
    type,
    assignedTo,
    status,
    created,
    completed,
    data,
    dueDate,
    text,
    kpi,
    herplanReference,
  } = activiteit
  const [isExpanded, setIsExpanded] = useState(false)
  const [logDrawer, setLogDrawer] = useState(false)
  const assignedToOrganisatie =
    assignedTo &&
    (assignedTo.includes(user.combi || '') ||
      assignedTo.includes(user.deelnemer || '') ||
      assignedTo.includes(user.delegerende || ''))
  const isCompleted = !!activiteit.completed?.date
  const isGraafmelding = type.toLowerCase() === 'graafmelding'
  const isVergunning = type.toLowerCase() === 'vergunning'
  const isStraatWerk = type.toLowerCase() === 'straatwerk'
  const changeIsExpanded = () => setIsExpanded(!isExpanded)
  const changeLogDrawer = () => setLogDrawer(!logDrawer)
  const editable =
    !isGraafmelding &&
    !isVergunning &&
    [user.combi, user.deelnemer, user.organisatie].includes(
      created.organisatie,
    ) &&
    !isCompleted

  return (
    <TableRow
      sx={{ ...(isCompleted && { backgroundColor: '#E0E0E0 !important' }) }}
    >
      <TableCell>
        <EntityKpi kpi={kpi} />
      </TableCell>
      <TableCell>
        {type}

        <br />
        <Typography variant="caption">{getStatusLabel(status)}</Typography>
      </TableCell>
      <TableCell>
        <ActiviteitOpmerking
          status={status}
          isVergunning={isVergunning}
          text={text}
          completed={completed}
          data={data}
        />
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box>{dueDate && <Due dueDate={dueDate} />}</Box>

            {herplanReference && !isCompleted && (
              <StickyDeadlineIcon herplanReference={herplanReference} />
            )}
          </Box>
          {assignedTo && <>{assignedTo.join(', ')}</>}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box component="span">{formatDateTime(created.date)}</Box>
          <Box component="span">{created.organisatie}</Box>

          {created.email && (
            <Typography variant="caption">{created.email}</Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        {editable && (
          <StyledIconButton
            title="Bewerken"
            onClick={handleClickEdit}
            icon={<EditIcon />}
          />
        )}
        {!isGraafmelding && !isVergunning && (
          <StyledIconButton
            title="Activiteit log"
            onClick={changeLogDrawer}
            icon={<InfoIcon />}
          />
        )}
        {assignedToOrganisatie && !isCompleted && !isStraatWerk && (
          <StyledIconButton
            title="Afhandelen"
            onClick={() =>
              handleClickFinish({ key: _id, type: 'resolve', activiteit })
            }
            icon={<PlaylistPlayIcon />}
          />
        )}

        {(user.organisatie === created.organisatie || isVerantwoordelijke) &&
          !isCompleted &&
          !isStraatWerk && (
            <StyledIconButton
              title="Annuleren"
              onClick={() =>
                handleClickCancel({ key: _id, type: 'cancel', activiteit })
              }
              icon={<CancelIcon />}
            />
          )}

        {isGraafmelding && (
          <>
            <StyledIconButton
              title="Informatie graafmelding"
              onClick={changeIsExpanded}
              icon={<InfoIcon />}
            />
            {data?.graafmelding?.portalUrl && (
              <Tooltip title="Bekijk deze graafmelding in het WoW-portaal">
                <a
                  href={data.graafmelding.portalUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconButton aria-label="Navigeer naar het WoW portaal">
                    {' '}
                    <LaunchIcon />{' '}
                  </IconButton>
                </a>
              </Tooltip>
            )}
          </>
        )}
        {isVergunning && (
          <>
            <StyledIconButton
              title="Informatie vergunning"
              onClick={changeIsExpanded}
              icon={<InfoIcon />}
            />
            {data?.vergunning?.portalUrl && (
              <Tooltip title="Bekijk deze vergunning in het WoW-portaal">
                <a
                  href={data.vergunning.portalUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconButton aria-label="Navigeer naar het WoW portaal">
                    {' '}
                    <LaunchIcon />{' '}
                  </IconButton>
                </a>
              </Tooltip>
            )}
          </>
        )}
        {isStraatWerk && data?.graafmelding?.portalUrl && (
          <Tooltip title="Bekijk deze graafmelding in het WoW-portaal">
            <a
              href={data.graafmelding.portalUrl}
              target="_blank"
              rel="noreferrer"
            >
              <IconButton aria-label="Navigeer naar het WoW portaal">
                {' '}
                <LaunchIcon />{' '}
              </IconButton>
            </a>
          </Tooltip>
        )}
        {isExpanded && (
          <WowDrawer
            isExpanded={isExpanded}
            activiteit={activiteit}
            onClose={changeIsExpanded}
          />
        )}
        {logDrawer && (
          <ActiviteitLog
            open={logDrawer}
            onClose={changeLogDrawer}
            activiteit={activiteit}
          />
        )}
      </TableCell>
    </TableRow>
  )
}
